import React, { useState, useMemo } from 'react';
import { X, Download, Search, SlidersHorizontal, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, HelpCircle } from 'lucide-react';
import StarDetailCard from './StarDetailCard';

// Tooltip Component
const Tooltip = ({ children, content, position = 'right' }) => {
  const [show, setShow] = useState(false);
  
  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {show && (
        <div className={`absolute z-50 w-64 p-3 bg-slate-800 border border-cyan-500/30 rounded-lg shadow-xl text-xs text-slate-200 leading-relaxed -top-2 ${
          position === 'left' ? 'right-full mr-2' : 'left-full ml-2'
        }`}>
          <div className={`absolute top-3 w-2 h-2 bg-slate-800 border border-cyan-500/30 transform rotate-45 ${
            position === 'left' 
              ? '-right-1 border-r border-t' 
              : '-left-1 border-l border-b'
          }`} />
          {content}
        </div>
      )}
    </div>
  );
};

const StarTable = ({ stars, onClose, runName, birthDate, placeAddress }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('magnitude'); // Default: brightest first
  const [sortDirection, setSortDirection] = useState('asc');
  const [filters, setFilters] = useState({
    objectType: 'all',
    constellation: 'all',
    spectralType: 'all',
    magnitudeMin: '',
    magnitudeMax: '',
    hasName: 'all'
  });
  const [showFilters, setShowFilters] = useState(false);
  const [hoveredStar, setHoveredStar] = useState(null);
  const [hoverPosition, setHoverPosition] = useState(null);
  
  const itemsPerPage = 50;

  // Get unique values for filter dropdowns
  const uniqueTypes = useMemo(() => {
    const types = [...new Set(stars.map(s => s.object_type || 'Star'))];
    return types.sort();
  }, [stars]);

  const uniqueConstellations = useMemo(() => {
    const constellations = [...new Set(stars.map(s => s.constellation).filter(Boolean))];
    return constellations.sort();
  }, [stars]);

  const uniqueSpectralTypes = useMemo(() => {
    const types = [...new Set(stars.map(s => s.spect_letter).filter(Boolean))];
    return types.sort();
  }, [stars]);

  // Calculate catalog ID counts
  const catalogCounts = useMemo(() => ({
    hip: stars.filter(s => s.hip).length,
    tyc: stars.filter(s => s.tyc).length,
    tmass: stars.filter(s => s.tmass).length,
    wise: stars.filter(s => s.wise).length,
    hd: stars.filter(s => s.hd).length,
    bayer: stars.filter(s => s.bayer_designation || s.designation).length,
  }), [stars]);

  // Calculate magnitude breakdown
  const magStats = useMemo(() => {
    const ranges = [
      { label: '1-7', min: 1, max: 7 },
      { label: '7-10', min: 7, max: 10 },
      { label: '10-13', min: 10, max: 13 },
      { label: '13-16', min: 13, max: 16 },
      { label: '16+', min: 16, max: 99 }
    ];
    return ranges.map(range => ({
      ...range,
      count: stars.filter(s => s.magnitude >= range.min && s.magnitude < range.max).length
    })).filter(r => r.count > 0);
  }, [stars]);

  // Calculate spectral type breakdown
  const spectralStats = useMemo(() => {
    const types = ['O', 'B', 'A', 'F', 'G', 'K', 'M'];
    return types.map(type => ({
      type,
      count: stars.filter(s => s.spect_letter === type).length
    })).filter(s => s.count > 0);
  }, [stars]);

  // Apply filters and search
  const filteredStars = useMemo(() => {
    return stars.filter(star => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = 
          (star.proper && star.proper.toLowerCase().includes(searchLower)) ||
          (star.display_name && star.display_name.toLowerCase().includes(searchLower)) ||
          (star.hd && star.hd.toString().includes(searchLower)) ||
          (star.hip && star.hip.toString().includes(searchLower)) ||
          (star.designation && star.designation.toLowerCase().includes(searchLower)) ||
          (star.common_name && star.common_name.toLowerCase().includes(searchLower)) ||
          (star.source_id && star.source_id.toLowerCase().includes(searchLower)) ||
          (star.constellation && star.constellation.toLowerCase().includes(searchLower)) ||
          (star.bayer_designation && star.bayer_designation.toLowerCase().includes(searchLower));
        
        if (!matchesSearch) return false;
      }

      // Object type filter
      if (filters.objectType !== 'all' && star.object_type !== filters.objectType) {
        return false;
      }

      // Constellation filter
      if (filters.constellation !== 'all' && star.constellation !== filters.constellation) {
        return false;
      }

      // Spectral type filter
      if (filters.spectralType !== 'all' && star.spect_letter !== filters.spectralType) {
        return false;
      }

      // Magnitude range filter
      if (filters.magnitudeMin !== '' && star.magnitude < parseFloat(filters.magnitudeMin)) {
        return false;
      }
      if (filters.magnitudeMax !== '' && star.magnitude > parseFloat(filters.magnitudeMax)) {
        return false;
      }

      // Has name filter
      if (filters.hasName === 'named' && !star.common_name) {
        return false;
      }
      if (filters.hasName === 'unnamed' && star.common_name) {
        return false;
      }

      return true;
    });
  }, [stars, searchTerm, filters]);

  // Apply sorting
  const sortedStars = useMemo(() => {
    const sorted = [...filteredStars];
    
    sorted.sort((a, b) => {
      let aVal, bVal;

      switch (sortField) {
        case 'magnitude':
          aVal = a.magnitude;
          bVal = b.magnitude;
          break;
        case 'distance':
          aVal = a.distance_ly || 999999;
          bVal = b.distance_ly || 999999;
          break;
        case 'name':
          aVal = a.proper || a.display_name || a.common_name || a.source_id;
          bVal = b.proper || b.display_name || b.common_name || b.source_id;
          break;
        case 'hd':
          aVal = a.hd || 999999;
          bVal = b.hd || 999999;
          break;
        case 'hip':
          aVal = a.hip || 999999;
          bVal = b.hip || 999999;
          break;
        case 'designation':
          aVal = a.designation || '';
          bVal = b.designation || '';
          break;
        case 'type':
          aVal = a.object_type || 'Star';
          bVal = b.object_type || 'Star';
          break;
        case 'constellation':
          aVal = a.constellation || '';
          bVal = b.constellation || '';
          break;
        case 'spectral':
          aVal = a.spect_letter || 'ZZZ';
          bVal = b.spect_letter || 'ZZZ';
          break;
        case 'ra':
          aVal = a.ra;
          bVal = b.ra;
          break;
        case 'dec':
          aVal = a.dec;
          bVal = b.dec;
          break;
        default:
          return 0;
      }

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });

    return sorted;
  }, [filteredStars, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(sortedStars.length / itemsPerPage);
  const paginatedStars = sortedStars.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Handle sort column click
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  // Get sort icon
  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-3 h-3 text-slate-500" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-3 h-3 text-cyan-400" />
      : <ArrowDown className="w-3 h-3 text-cyan-400" />;
  };

  // Download CSV
  const handleDownloadCSV = () => {
    const headers = [
      'Name', 'Source ID', 'Type', 'Constellation', 'Magnitude', 'Distance (ly)', 
      'RA', 'Dec', 'HIP', 'TYC', '2MASS', 'WISE', 'Pan-STARRS', 'SDSS',
      'Spectral', 'Temp (K)', 'Color (BP-RP)', 'Proper', 'Designation',
      'Prob Star', 'Prob Quasar', 'Prob Galaxy', 'PMRA', 'PMDEC'
    ];
    
    const rows = sortedStars.map(star => [
      star.display_name || star.proper || `Gaia ${star.source_id}`,
      star.source_id,
      star.object_type,
      star.constellation || '',
      star.magnitude.toFixed(2),
      star.distance_ly?.toFixed(1) || '',
      star.ra.toFixed(4),
      star.dec.toFixed(4),
      star.hip || '',
      star.tyc || '',
      star.tmass || '',
      star.wise || '',
      star.panstarrs || '',
      star.sdss || '',
      star.spect_letter || '',
      star.teff?.toFixed(0) || '',
      star.bp_rp?.toFixed(3) || '',
      star.proper || '',
      star.designation || '',
      star.prob_star?.toFixed(4) || '',
      star.prob_quasar?.toFixed(4) || '',
      star.prob_galaxy?.toFixed(4) || '',
      star.pmra?.toFixed(4) || '',
      star.pmdec?.toFixed(4) || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `star-table-${runName || 'export'}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      objectType: 'all',
      constellation: 'all',
      spectralType: 'all',
      magnitudeMin: '',
      magnitudeMax: '',
      hasName: 'all'
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-2xl w-full max-w-7xl max-h-[90vh] flex flex-col shadow-2xl">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex-1">
            <h2 className="font-unbounded text-2xl text-white flex items-center gap-3">
              ⭐ Celestial Objects for <span className="text-cyan-400">{runName || 'Unknown Location'}</span>
            </h2>
            <div className="text-sm text-slate-400 mt-2 space-y-1">
              {placeAddress && <div>{placeAddress}</div>}
              {birthDate && <div>{new Date(birthDate).toLocaleString()}</div>}
              <div className="flex items-center gap-4 text-xs">
                <span>{sortedStars.length.toLocaleString()} objects {sortedStars.length !== stars.length && `(filtered from ${stars.length.toLocaleString()})`}</span>
                {stars[0]?.ra && stars[0]?.dec && (
                  <span className="text-slate-500">
                    RA: {Math.min(...stars.map(s => s.ra)).toFixed(2)}° - {Math.max(...stars.map(s => s.ra)).toFixed(2)}° • 
                    Dec: {Math.min(...stars.map(s => s.dec)).toFixed(2)}° - {Math.max(...stars.map(s => s.dec)).toFixed(2)}°
                  </span>
                )}
              </div>
            </div>
            
            {/* Object Type Breakdown */}
            <div className="flex flex-wrap gap-2 mt-3 text-xs">
              <span className="text-slate-500">Objects:</span>
              <span className="px-2 py-1 bg-indigo-500/30 text-indigo-300 rounded">
                {stars.filter(s => s.object_type === 'Star').length.toLocaleString()} Stars
              </span>
              {stars.filter(s => s.object_type === 'Quasar').length > 0 && (
                <span className="px-2 py-1 bg-purple-500/30 text-purple-300 rounded">
                  {stars.filter(s => s.object_type === 'Quasar').length} Quasars
                </span>
              )}
              {stars.filter(s => s.object_type === 'Galaxy').length > 0 && (
                <span className="px-2 py-1 bg-pink-500/30 text-pink-300 rounded">
                  {stars.filter(s => s.object_type === 'Galaxy').length} Galaxies
                </span>
              )}
            </div>
            
            {/* Magnitude Breakdown */}
            {magStats.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2 text-xs">
                <span className="text-slate-500">Magnitude:</span>
                {magStats.map(stat => (
                  <span key={stat.label} className="px-2 py-1 bg-indigo-500/20 text-indigo-300 rounded">
                    <span className="text-slate-400">{stat.label}:</span> <span className="font-bold">{stat.count.toLocaleString()}</span> <span className="text-slate-500 text-[10px]">({(stat.count / stars.length * 100).toFixed(1)}%)</span>
                  </span>
                ))}
              </div>
            )}
            
            {/* Spectral Breakdown */}
            {spectralStats.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2 text-xs">
                <span className="text-slate-500">Spectral:</span>
                {spectralStats.map(stat => (
                  <span 
                    key={stat.type} 
                    className="px-2 py-1 rounded font-bold"
                    style={{
                      backgroundColor: stat.type === 'O' || stat.type === 'B' ? 'rgba(74, 144, 226, 0.3)' :
                                     stat.type === 'A' ? 'rgba(255, 255, 255, 0.2)' :
                                     stat.type === 'F' || stat.type === 'G' ? 'rgba(253, 184, 19, 0.3)' :
                                     stat.type === 'K' ? 'rgba(255, 127, 80, 0.3)' :
                                     'rgba(255, 69, 0, 0.3)',
                      color: stat.type === 'O' || stat.type === 'B' ? '#6BB6FF' :
                           stat.type === 'A' ? '#FFFFFF' :
                           stat.type === 'F' || stat.type === 'G' ? '#FDB813' :
                           stat.type === 'K' ? '#FF7F50' :
                           '#FF4500'
                    }}
                  >
                    {stat.type}: {stat.count.toLocaleString()} <span className="text-[10px] opacity-70">({(stat.count / stars.length * 100).toFixed(1)}%)</span>
                  </span>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-slate-400" />
          </button>
        </div>

        {/* Controls */}
        <div className="p-6 border-b border-white/10 space-y-4">
          {/* Search and Filter Toggle */}
          <div className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name, constellation, or ID..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="w-full pl-10 pr-4 py-2 bg-black/30 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
              />
            </div>
            
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                showFilters 
                  ? 'bg-cyan-600 text-white' 
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filters
            </button>

            <button
              onClick={handleDownloadCSV}
              className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg flex items-center gap-2 transition-colors"
            >
              <Download className="w-4 h-4" />
              Download CSV
            </button>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3 p-4 bg-black/20 rounded-lg border border-slate-700">
              {/* Object Type Filter */}
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Object Type</label>
                <select
                  value={filters.objectType}
                  onChange={(e) => { setFilters({...filters, objectType: e.target.value}); setCurrentPage(1); }}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-cyan-500"
                >
                  <option value="all">All Types</option>
                  {uniqueTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Constellation Filter */}
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Constellation</label>
                <select
                  value={filters.constellation}
                  onChange={(e) => { setFilters({...filters, constellation: e.target.value}); setCurrentPage(1); }}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-cyan-500"
                >
                  <option value="all">All Constellations</option>
                  {uniqueConstellations.map(constellation => (
                    <option key={constellation} value={constellation}>{constellation}</option>
                  ))}
                </select>
              </div>

              {/* Spectral Type Filter */}
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Spectral Type</label>
                <select
                  value={filters.spectralType}
                  onChange={(e) => { setFilters({...filters, spectralType: e.target.value}); setCurrentPage(1); }}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-cyan-500"
                >
                  <option value="all">All Types</option>
                  {uniqueSpectralTypes.map(type => (
                    <option key={type} value={type}>{type} - {type === 'O' ? 'Blue' : type === 'B' ? 'Blue' : type === 'A' ? 'White' : type === 'F' ? 'Yellow' : type === 'G' ? 'Yellow' : type === 'K' ? 'Orange' : type === 'M' ? 'Red' : 'Unknown'}</option>
                  ))}
                </select>
              </div>

              {/* Magnitude Min */}
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Min Magnitude</label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="e.g. 0"
                  value={filters.magnitudeMin}
                  onChange={(e) => { setFilters({...filters, magnitudeMin: e.target.value}); setCurrentPage(1); }}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-cyan-500"
                />
              </div>

              {/* Magnitude Max */}
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Max Magnitude</label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="e.g. 10"
                  value={filters.magnitudeMax}
                  onChange={(e) => { setFilters({...filters, magnitudeMax: e.target.value}); setCurrentPage(1); }}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-cyan-500"
                />
              </div>

              {/* Has Name Filter */}
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Named Objects</label>
                <select
                  value={filters.hasName}
                  onChange={(e) => { setFilters({...filters, hasName: e.target.value}); setCurrentPage(1); }}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-cyan-500"
                >
                  <option value="all">All</option>
                  <option value="named">Named Only</option>
                  <option value="unnamed">Unnamed Only</option>
                </select>
              </div>

              {/* Reset Button */}
              <div className="md:col-span-3 lg:col-span-6 flex justify-end">
                <button
                  onClick={handleResetFilters}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded transition-colors"
                >
                  Reset Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-800 border-b border-white/10 z-10">
              <tr>
                <th className="text-left py-3 px-4 font-mono text-xs text-slate-400 uppercase cursor-pointer hover:bg-white/5"
                    onClick={() => handleSort('name')}>
                  <div className="flex items-center gap-2">
                    Proper Name
                    {getSortIcon('name')}
                  </div>
                </th>
                <th className="text-left py-3 px-4 font-mono text-xs text-slate-400 uppercase cursor-pointer hover:bg-white/5"
                    onClick={() => handleSort('hd')}>
                  <div className="flex flex-col items-start gap-1">
                    <div className="flex items-center gap-2">
                      HD
                      {getSortIcon('hd')}
                    </div>
                    {catalogCounts.hd > 0 && (
                      <span className="text-[10px] text-cyan-400 font-normal">{catalogCounts.hd.toLocaleString()}</span>
                    )}
                  </div>
                </th>
                <th className="text-left py-3 px-4 font-mono text-xs text-slate-400 uppercase cursor-pointer hover:bg-white/5"
                    onClick={() => handleSort('hip')}>
                  <div className="flex flex-col items-start gap-1">
                    <div className="flex items-center gap-2">
                      HIP
                      {getSortIcon('hip')}
                    </div>
                    {catalogCounts.hip > 0 && (
                      <span className="text-[10px] text-cyan-400 font-normal">{catalogCounts.hip.toLocaleString()}</span>
                    )}
                  </div>
                </th>
                <th className="text-left py-3 px-4 font-mono text-xs text-slate-400 uppercase cursor-pointer hover:bg-white/5"
                    onClick={() => handleSort('tyc')}>
                  <div className="flex flex-col items-start gap-1">
                    <div className="flex items-center gap-2">
                      <Tooltip content="Tycho-2 catalog number (~2.5M stars). Major astrometric catalog from the Hipparcos mission.">
                        <span className="flex items-center gap-1">
                          TYC
                          <HelpCircle className="w-3 h-3 text-slate-500" />
                        </span>
                      </Tooltip>
                      {getSortIcon('tyc')}
                    </div>
                    {catalogCounts.tyc > 0 && (
                      <span className="text-[10px] text-purple-400 font-normal">{catalogCounts.tyc.toLocaleString()}</span>
                    )}
                  </div>
                </th>
                <th className="text-left py-3 px-4 font-mono text-xs text-slate-400 uppercase cursor-pointer hover:bg-white/5"
                    onClick={() => handleSort('tmass')}>
                  <div className="flex flex-col items-start gap-1">
                    <div className="flex items-center gap-2">
                      <Tooltip content="2MASS infrared survey ID. Near-infrared all-sky catalog.">
                        <span className="flex items-center gap-1">
                          2MASS
                          <HelpCircle className="w-3 h-3 text-slate-500" />
                        </span>
                      </Tooltip>
                      {getSortIcon('tmass')}
                    </div>
                    {catalogCounts.tmass > 0 && (
                      <span className="text-[10px] text-orange-400 font-normal">{catalogCounts.tmass.toLocaleString()}</span>
                    )}
                  </div>
                </th>
                <th className="text-left py-3 px-4 font-mono text-xs text-slate-400 uppercase cursor-pointer hover:bg-white/5"
                    onClick={() => handleSort('wise')}>
                  <div className="flex flex-col items-start gap-1">
                    <div className="flex items-center gap-2">
                      <Tooltip content="WISE infrared survey ID. Wide-field infrared explorer catalog.">
                        <span className="flex items-center gap-1">
                          WISE
                          <HelpCircle className="w-3 h-3 text-slate-500" />
                        </span>
                      </Tooltip>
                      {getSortIcon('wise')}
                    </div>
                    {catalogCounts.wise > 0 && (
                      <span className="text-[10px] text-pink-400 font-normal">{catalogCounts.wise.toLocaleString()}</span>
                    )}
                  </div>
                </th>
                <th className="text-left py-3 px-4 font-mono text-xs text-slate-400 uppercase cursor-pointer hover:bg-white/5"
                    onClick={() => handleSort('designation')}>
                  <div className="flex flex-col items-start gap-1">
                    <div className="flex items-center gap-2">
                      Bayer/Flamsteed
                      {getSortIcon('designation')}
                    </div>
                    {catalogCounts.bayer > 0 && (
                      <span className="text-[10px] text-amber-400 font-normal">{catalogCounts.bayer.toLocaleString()}</span>
                    )}
                  </div>
                </th>
                <th className="text-left py-3 px-4 font-mono text-xs text-slate-400 uppercase cursor-pointer hover:bg-white/5"
                    onClick={() => handleSort('type')}>
                  <div className="flex items-center gap-2">
                    <Tooltip content="Object classification from Gaia Data Science Collaboration. Stars, Quasars, and Galaxies are distinguished by their spectral signatures and probabilities.">
                      <span className="flex items-center gap-1">
                        Type
                        <HelpCircle className="w-3 h-3 text-slate-500" />
                      </span>
                    </Tooltip>
                    {getSortIcon('type')}
                  </div>
                </th>
                <th className="text-left py-3 px-4 font-mono text-xs text-slate-400 uppercase cursor-pointer hover:bg-white/5"
                    onClick={() => handleSort('constellation')}>
                  <div className="flex items-center gap-2">
                    <Tooltip content="The IAU (International Astronomical Union) constellation this star belongs to, based on the official 1930 boundaries. Shows the constellation at your birth.">
                      <span className="flex items-center gap-1">
                        Constellation
                        <HelpCircle className="w-3 h-3 text-slate-500" />
                      </span>
                    </Tooltip>
                    {getSortIcon('constellation')}
                  </div>
                </th>
                <th className="text-left py-3 px-4 font-mono text-xs text-slate-400 uppercase cursor-pointer hover:bg-white/5"
                    onClick={() => handleSort('spectral')}>
                  <div className="flex items-center gap-2">
                    <Tooltip content="Spectral classification (O/B/A/F/G/K/M): O & B are hot blue stars, A is white (Vega/Sirius), F & G are yellow (Sun), K is orange, M is cool red. The sequence represents temperature from hottest (O) to coolest (M).">
                      <span className="flex items-center gap-1">
                        Spectral
                        <HelpCircle className="w-3 h-3 text-slate-500" />
                      </span>
                    </Tooltip>
                    {getSortIcon('spectral')}
                  </div>
                </th>
                <th className="text-left py-3 px-4 font-mono text-xs text-slate-400 uppercase cursor-pointer hover:bg-white/5"
                    onClick={() => handleSort('magnitude')}>
                  <div className="flex items-center gap-2">
                    <Tooltip content="Apparent brightness as seen from Earth. Lower numbers are brighter (Vega is 0.0, the Sun is -26.7). A magnitude 1 star is 2.5× brighter than magnitude 2.">
                      <span className="flex items-center gap-1">
                        Magnitude
                        <HelpCircle className="w-3 h-3 text-slate-500" />
                      </span>
                    </Tooltip>
                    {getSortIcon('magnitude')}
                  </div>
                </th>
                <th className="text-left py-3 px-4 font-mono text-xs text-slate-400 uppercase cursor-pointer hover:bg-white/5"
                    onClick={() => handleSort('distance')}>
                  <div className="flex items-center gap-2">
                    <Tooltip content="Distance in light-years (the distance light travels in one year = 6 trillion miles). Calculated from parallax—the tiny wobble in the star's position as Earth orbits the Sun.">
                      <span className="flex items-center gap-1">
                        Distance (ly)
                        <HelpCircle className="w-3 h-3 text-slate-500" />
                      </span>
                    </Tooltip>
                    {getSortIcon('distance')}
                  </div>
                </th>
                <th className="text-left py-3 px-4 font-mono text-xs text-slate-400 uppercase cursor-pointer hover:bg-white/5"
                    onClick={() => handleSort('ra')}>
                  <div className="flex items-center gap-2">
                    <Tooltip 
                      position="left"
                      content={`Right Ascension: The star's East-West position in the sky (celestial longitude). Measured in degrees (0-360°). ${birthDate ? `Position at your birth: ${birthDate}` : 'Position at birth moment'}`}
                    >
                      <span className="flex items-center gap-1">
                        RA
                        <HelpCircle className="w-3 h-3 text-slate-500" />
                      </span>
                    </Tooltip>
                    {getSortIcon('ra')}
                  </div>
                </th>
                <th className="text-left py-3 px-4 font-mono text-xs text-slate-400 uppercase cursor-pointer hover:bg-white/5"
                    onClick={() => handleSort('dec')}>
                  <div className="flex items-center gap-2">
                    <Tooltip 
                      position="left"
                      content={`Declination: The star's North-South position in the sky (celestial latitude). From +90° (North Celestial Pole) to -90° (South Celestial Pole). ${birthDate ? `Position at your birth: ${birthDate}` : 'Position at birth moment'}`}
                    >
                      <span className="flex items-center gap-1">
                        Dec
                        <HelpCircle className="w-3 h-3 text-slate-500" />
                      </span>
                    </Tooltip>
                    {getSortIcon('dec')}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedStars.map((star, idx) => (
                <tr 
                  key={star.source_id} 
                  className="border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer"
                  onClick={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setHoverPosition({ x: rect.right + 10, y: rect.top });
                    setHoveredStar(star);
                  }}
                  title="Click to see movement details"
                >
                  <td className="py-3 px-4">
                    {star.proper || star.display_name ? (
                      <div>
                        <div className="text-yellow-300 font-medium">{star.proper || star.display_name}</div>
                        {star.proper && <div className="text-xs text-slate-500 font-mono">{star.source_id}</div>}
                      </div>
                    ) : (
                      <span className="text-slate-500 italic text-xs">-</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    {star.hd ? (
                      <span className="text-cyan-300 font-mono text-sm">HD {star.hd}</span>
                    ) : (
                      <span className="text-slate-500 italic text-xs">-</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    {star.hip ? (
                      <span className="text-cyan-300 font-mono text-sm">HIP {star.hip}</span>
                    ) : (
                      <span className="text-slate-500 italic text-xs">-</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    {star.tyc ? (
                      <span className="text-purple-300 font-mono text-xs">{star.tyc}</span>
                    ) : (
                      <span className="text-slate-500 italic text-xs">-</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    {star.tmass ? (
                      <span className="text-orange-300 font-mono text-xs">{star.tmass}</span>
                    ) : (
                      <span className="text-slate-500 italic text-xs">-</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    {star.wise ? (
                      <span className="text-pink-300 font-mono text-xs">{star.wise}</span>
                    ) : (
                      <span className="text-slate-500 italic text-xs">-</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    {star.designation || star.bayer_greek ? (
                      <span className="text-amber-300 text-sm">{star.designation || star.bayer_greek}</span>
                    ) : (
                      <span className="text-slate-500 italic text-xs">-</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded text-xs ${
                      star.object_type === 'Quasar' ? 'bg-purple-500/20 text-purple-300' :
                      star.object_type === 'Galaxy' ? 'bg-pink-500/20 text-pink-300' :
                      'bg-cyan-500/20 text-cyan-300'
                    }`}>
                      {star.object_type || 'Star'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-indigo-300">
                    {star.constellation || 'N/A'}
                  </td>
                  <td className="py-3 px-4">
                    {star.spect_letter ? (
                      <span 
                        className="px-2 py-1 rounded text-xs font-mono font-bold"
                        style={{ 
                          backgroundColor: `${star.spectral_color}20`,
                          color: star.spectral_color,
                          border: `1px solid ${star.spectral_color}50`
                        }}
                      >
                        {star.spect_letter}
                      </span>
                    ) : (
                      <span className="text-slate-500 italic text-xs">-</span>
                    )}
                  </td>
                  <td className="py-3 px-4 font-mono text-white">
                    {star.magnitude.toFixed(2)}
                  </td>
                  <td className="py-3 px-4 font-mono text-slate-300">
                    {star.distance_ly ? star.distance_ly.toFixed(1) : 'N/A'}
                  </td>
                  <td className="py-3 px-4 font-mono text-slate-400 text-xs">
                    {star.ra.toFixed(4)}°
                  </td>
                  <td className="py-3 px-4 font-mono text-slate-400 text-xs">
                    {star.dec.toFixed(4)}°
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {paginatedStars.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <p>No objects match your filters</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-white/10 flex items-center justify-between">
            <div className="text-sm text-slate-400">
              Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, sortedStars.length)} of {sortedStars.length.toLocaleString()}
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-slate-400" />
              </button>
              
              <div className="text-sm text-white font-mono">
                Page {currentPage} of {totalPages}
              </div>
              
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="p-2 rounded hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Star Detail Card - Movement Analytics */}
      {hoveredStar && birthDate && (
        <StarDetailCard
          star={hoveredStar}
          birthDate={birthDate}
          onClose={() => setHoveredStar(null)}
          position={hoverPosition}
        />
      )}
    </div>
  );
};

export default StarTable;
